import { Link } from 'react-router-dom';
import { Shield, Database, Send, Share2, Clock, UserCheck, Lock, Baby, RefreshCw, Mail } from 'lucide-react';
import Reveal from '../components/Reveal';
import { LEGAL } from '../lib/legal';

/**
 * Public Privacy Policy — reachable at /privacy without any login, as required
 * by Google Play. Content mirrors what the API actually stores (see the Inquiry
 * model on the server); keep the two in sync if the schema changes.
 */
const sections = [
  {
    icon: Database,
    title: 'Information We Collect',
    body: (
      <>
        <p>
          {LEGAL.brand} does not require you to create an account. We collect information
          only when you choose to send us an enquiry through the app or website:
        </p>
        <ul>
          <li><strong>Full name</strong> and <strong>mobile number</strong> — required, so we can respond to you.</li>
          <li><strong>Email address</strong> — optional.</li>
          <li><strong>Event details</strong> — event date, location, and any notes you write.</li>
          <li><strong>Your selections</strong> — the package and add-ons you picked, and the estimated total.</li>
          <li><strong>Source</strong> — whether the enquiry came from our app or website, and the date it was sent.</li>
        </ul>
        <p>
          We do <strong>not</strong> collect payment card details, precise device location,
          contacts, photos, or advertising identifiers through this app.
        </p>
      </>
    ),
  },
  {
    icon: Send,
    title: 'How We Use Your Information',
    body: (
      <ul>
        <li>To contact you about the enquiry you submitted and answer your questions.</li>
        <li>To prepare a quote for the event and packages you selected.</li>
        <li>To organise and deliver services if you decide to book with us.</li>
        <li>To keep internal records of enquiries and their status.</li>
      </ul>
    ),
  },
  {
    icon: Share2,
    title: 'WhatsApp and Third Parties',
    body: (
      <>
        <p>
          When you submit an enquiry, the app opens <strong>WhatsApp</strong> with a summary
          message so you can continue the conversation with our team. Anything you send there
          is handled by WhatsApp under its own privacy policy, and we receive it as a normal
          WhatsApp business chat.
        </p>
        <p>We also rely on service providers who process data on our behalf:</p>
        <ul>
          <li>Cloud hosting and database providers that store enquiry records.</li>
          <li>An email provider used to notify our team when a new enquiry arrives.</li>
        </ul>
        <p>
          We do <strong>not</strong> sell your personal information, and we do not share it for
          advertising purposes.
        </p>
      </>
    ),
  },
  {
    icon: Clock,
    title: 'How Long We Keep It',
    body: (
      <p>
        Enquiry records are kept for up to <strong>{LEGAL.retentionMonths} months</strong> so we
        can service repeat customers and maintain business records, unless you ask us to delete
        them sooner. Some information may be retained longer where the law requires it — for
        example, invoices and tax records for a completed booking.
      </p>
    ),
  },
  {
    icon: UserCheck,
    title: 'Your Rights and Choices',
    body: (
      <>
        <p>You may ask us to:</p>
        <ul>
          <li>Tell you what personal information we hold about you.</li>
          <li>Correct anything that is wrong or out of date.</li>
          <li>Delete your information — see our <Link to="/delete" className="text-brand hover:underline">Data Deletion page</Link>.</li>
          <li>Stop contacting you.</li>
        </ul>
        <p>
          We respond to requests within <strong>{LEGAL.deletionSlaDays} days</strong>.
        </p>
      </>
    ),
  },
  {
    icon: Lock,
    title: 'Security',
    body: (
      <p>
        Enquiries are transmitted over encrypted HTTPS connections and stored in an
        access-controlled database. Administrator accounts are protected by passwords that are
        stored only as salted hashes, never in plain text. No method of transmission or storage
        is completely secure, so we cannot guarantee absolute security.
      </p>
    ),
  },
  {
    icon: Baby,
    title: "Children's Privacy",
    body: (
      <p>
        Our services are intended for adults arranging events. We do not knowingly collect
        personal information from children. If you believe a child has sent us their details,
        contact us and we will delete the record.
      </p>
    ),
  },
  {
    icon: RefreshCw,
    title: 'Changes to This Policy',
    body: (
      <p>
        We may update this policy from time to time. The revised version will always be
        available at this address, with a new effective date at the top of the page.
      </p>
    ),
  },
];

export default function Privacy() {

  return (
    <div className="section py-12">
      <Reveal className="mx-auto max-w-3xl text-center">
        <span className="mx-auto mb-5 grid h-14 w-14 place-items-center rounded-2xl bg-brand-gradient text-white">
          <Shield size={26} />
        </span>
        <h1 className="font-display text-4xl font-extrabold">
          Privacy <span className="text-gradient">Policy</span>
        </h1>
        <p className="mt-4 text-ink-300">
          How {LEGAL.brand} collects, uses, and protects your information.
        </p>
        <p className="mt-2 text-sm text-ink-400">Effective {LEGAL.effectiveDate}</p>
      </Reveal>

      <div className="mx-auto mt-12 max-w-3xl space-y-6">
        {sections.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.05} className="glass p-6 sm:p-8">
            <div className="mb-4 flex items-center gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white/5 text-brand">
                <s.icon size={20} />
              </span>
              <h2 className="font-display text-xl font-bold">{s.title}</h2>
            </div>
            <div className="policy-prose">{s.body}</div>
          </Reveal>
        ))}

        <Reveal delay={0.05} className="glass p-6 sm:p-8">
          <div className="mb-4 flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white/5 text-brand">
              <Mail size={20} />
            </span>
            <h2 className="font-display text-xl font-bold">Contact Us</h2>
          </div>
          <div className="policy-prose">
            <p>Questions about this policy or your data:</p>
            <ul>
              <li><strong>{LEGAL.legalEntity}</strong></li>
              <li>Email: <a href={`mailto:${LEGAL.contactEmail}`} className="text-brand hover:underline">{LEGAL.contactEmail}</a></li>
              <li>Phone: {LEGAL.phone}</li>
              <li>{LEGAL.address}</li>
            </ul>
          </div>
        </Reveal>

        <Reveal className="text-center">
          <Link to="/delete" className="btn-primary">
            Request Data Deletion
          </Link>
        </Reveal>
      </div>
    </div>
  );
}
